import { includes } from "lodash";
import {
  clearAccessToken,
  clearFarmUserData,
  clearIndexDB,
  clearLocalStorage,
  deleteData,
  getData,
  postData,
} from "./common.service";

// Define the User interface
interface User {
  farmUserId: string;
  name: string;
  mobile: string;
  countryCode: string;
}

// Define a new interface for formatted users
interface FormattedUser {
  farmUserId: string;
  username: string;
  mobile: string;
}

export async function getUsers(text: string = "", skip = 0) {
  const qs = {
    where: {
      or: [
        { farmUserId: { like: `.*${text}.*`, options: "i" } },
        { name: { like: `.*${text}.*`, options: "i" } },
        { mobile: { like: `.*${text}.*`, options: "i" } },
        { countryCode: { like: `.*${text}.*`, options: "i" } },
      ],
    },
    fields: [
      "farmUserId",
      "name",
      "mobile",
      "countryCode",
      "orgId",
      "created_date",
    ],
    order: "created_date DESC",
    limit: 20,
    skip: skip,
  };
  return getData("/farm-users?filter=" + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

export async function searchUsersByMobile(mobile: string, skip = 0) {
  const qs = {
    where: { mobile: { like: `.*${mobile}.*`, options: "i" } },
    fields: [
      "farmUserId",
      "name",
      "mobile",
      "countryCode",
      "orgId",
      "created_date",
    ],
    order: "created_date DESC",
    limit: 20,
    skip: skip,
  };
  return getData("/farm-users?filter=" + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

export async function getUser(userId: string) {
  const qs = {
    include: ["roles"],
  };
  return getData(`/farm-users/${userId}?filter=` + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

// Simplified function without additional filtering
export async function getUsersWithRolesSupport(
  roleId?: string
): Promise<FormattedUser[]> {

  if (!roleId) {
    roleId = "support";
  }

  return getData(`/roles/${roleId}/users`, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

export async function searchUser(userId: string) {
  const qs = {
    fields: {
      farmUserId: true,
    },
  };
  return getData(`/farm-users/${userId}?filter=` + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  })
    .then((data) => data.json())
    .catch((e) => console.log(e));
}

export async function getUserByMobile(mobile: string) {
  const qs = {
    where: {
      mobile: mobile
    },
    fields: ["farmUserId", "name", "mobile", "countryCode"],
    limit: 1
  };
  return getData("/farm-users?filter=" + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

export async function generateOTP(userId: string) {
  return postData("/farm-users/generate-otp", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: { userId: userId },
  });
}
export async function generateOTPNoSend(userId: string) {
  return postData("/farm-users/generate-otp-no-send", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: { userId: userId },
  });
}
export async function login(credentials: any) {
  return postData("/farm-users/login", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: credentials,
  });
}
export async function logoutUser() {
  return postData("/farm-users/logout", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then(() => {
    clearLocalStorage();
    clearIndexDB();
  });
}

export async function getUserProfile() {
  const farmUserId = localStorage.getItem("farmUserId");
  const qs = {
    include: ["roles", "farms"],
  };
  return getData(`/farm-users/${farmUserId}?filter=` + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  });
}

export async function registerUser(userData: any) {
  return postData("/farm-users/register-user", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: userData,
  });
}

export async function updateUser(userData: any) {
  delete userData.edit;
  return postData("/farm-users/" + userData.farmUserId, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: userData,
    usePutMethod: true,
  });
}

export async function getRoles() {
  // Return static list of roles instead of API call
  return Promise.resolve([
    { id: "farmManager", name: "farmManager" },
    { id: "farmuser", name: "farmuser" },
    { id: "support", name: "support" },
    { id: "dealer", name: "dealer" }
  ]);
}

export async function getUserRoles(userId: string) {
  return getData(`/farm-users/${userId}/roles`, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

export async function saveRole(userId: string, roleId: string) {
  console.log("saveRole called with userId:", userId, "and roleId:", roleId);
  return postData(`/farm-users/${userId}/roles/${roleId}`, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    usePutMethod: true,
  });
}

export async function deleteRole(userId: string, roleId: string | number) {
  return deleteData(`/farm-users/${userId}/roles/${roleId}`, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  });
}

export async function isUserRegistered(payload: any) {
  const url = "/farm-users/is-user-registered";
  return postData(url, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    body: payload,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((data) => data.json());
}
